/**
 * MessageService - Central message management for multi-channel parent communications
 *
 * Handles CRUD operations for messages without delivery logic.
 * Delivery is handled by channel-specific services (TelegramService, EmailService, etc.)
 *
 * Features:
 * - Create and queue messages
 * - Track delivery status per channel
 * - Record read receipts and responses
 * - Query messages by parent, type, status
 * - Auto-compute engagement metrics
 *
 * @module services/communication/MessageService
 */

import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import {
  createMessageDocument,
  computeOverallStatus,
  isExpired,
  MESSAGE_TYPES,
  MESSAGE_STATUSES,
  CHANNELS
} from '../../models/Message';
class MessageService {
  constructor() {
    this.parentLinksCollection = 'parentChildLinks';
  }

  /**
   * Get messages collection reference (hierarchical path: schools/{schoolId}/messages)
   * @param {string} schoolId - Required
   * @returns {CollectionReference}
   * @private
   */
  _getMessagesCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('[MessageService] schoolId required for messages collection path');
    }
    return collection(db, 'schools', schoolId, 'messages');
  }

  /**
   * Get message document reference (hierarchical path: schools/{schoolId}/messages/{messageId})
   * @param {string} messageId - Message document ID
   * @param {string} schoolId - Required
   * @returns {DocumentReference}
   * @private
   */
  _getMessageDocRef(messageId, schoolId) {
    if (!schoolId) {
      throw new Error('[MessageService] schoolId required for message document path');
    }
    return doc(db, 'schools', schoolId, 'messages', messageId);
  }

  /**
   * Create a new message and queue it for delivery
   * @param {Object} messageData - Message content and metadata
   * @param {string} messageData.schoolId
   * @param {string} messageData.parentId
   * @param {string} messageData.studentId
   * @param {string} messageData.type - Message type
   * @param {string} messageData.priority - Message priority
   * @param {Object} messageData.content - { subject, body, summary }
   * @param {Array} [messageData.actions] - Interactive action buttons
   * @param {Date} [messageData.expiresAt] - Optional expiration date
   * @param {string} [messageData.teacherId] - Teacher who triggered message
   * @param {string} [messageData.mentionId] - Related mention/incident ID
   * @param {Object} [messageData.metadata] - Type-specific metadata
   * @param {string} messageData.createdBy - User ID creating the message
   * @returns {Promise<string>} messageId
   */
  async createMessage(messageData) {
    try {
      // Verify parent-student relationship
      const linkExists = await this.verifyParentStudentLink(
        messageData.parentId,
        messageData.studentId
      );

      if (!linkExists) {
        throw new Error('Invalid parent-student relationship');
      }

      // Initialize delivery channels based on parent's verified channels
      const enabledChannels = await this.initializeDeliveryChannels(messageData.parentId);

      // Create message document
      const messageDoc = createMessageDocument({
        ...messageData,
        enabledChannels
      });

      // Set server timestamps
      messageDoc.createdAt = serverTimestamp();
      messageDoc.updatedAt = serverTimestamp();

      // Set initial status to queued (ready for delivery)
      messageDoc.status = MESSAGE_STATUSES.QUEUED;

      // Validate schoolId present
      if (!messageData.schoolId) {
        throw new Error('schoolId is required for message creation');
      }

      // Add to Firestore (primary write - new or old path based on flag)
      const collectionRef = this._getMessagesCollectionRef(messageData.schoolId);
      const docRef = await addDoc(collectionRef, messageDoc);

      console.log(`Message created: ${docRef.id} for parent ${messageData.parentId} (school: ${messageData.schoolId})`);

      // Track engagement metrics for enabled channels (async, non-blocking)
      Object.entries(enabledChannels).forEach(([channel, enabled]) => {
        if (enabled) {
          this.updateParentEngagement(
            messageData.parentId,
            messageData.studentId,
            channel,
            'sent'
          ).catch(err => console.error(`Failed to track engagement for ${channel}:`, err));
        }
      });

      return docRef.id;

    } catch (error) {
      console.error('Error creating message:', error);
      throw new Error(`Failed to create message: ${error.message}`);
    }
  }

  /**
   * Verify parent-student relationship exists
   * @param {string} parentId
   * @param {string} studentId
   * @returns {Promise<boolean>}
   */
  async verifyParentStudentLink(parentId, studentId) {
    try {
      const linksQuery = query(
        collection(db, this.parentLinksCollection),
        where('parentId', '==', parentId),
        where('studentId', '==', studentId),
        where('status', '==', 'active'),
        limit(1)
      );

      const snapshot = await getDocs(linksQuery);
      return !snapshot.empty;

    } catch (error) {
      console.error('Error verifying parent-student link:', error);
      return false;
    }
  }

  /**
   * Initialize delivery channels based on parent's verified channels
   * Checks ALL parentChildLinks for the parent (not just first one)
   * @param {string} parentId
   * @returns {Promise<Object>} { telegram: boolean, inApp: boolean, email: boolean, sms: boolean }
   */
  async initializeDeliveryChannels(parentId) {
    try {
      // Query ALL parentChildLinks for this parent (they may have multiple children)
      const linksQuery = query(
        collection(db, this.parentLinksCollection),
        where('parentId', '==', parentId),
        where('status', '==', 'active')
      );

      const snapshot = await getDocs(linksQuery);

      // Default: only inApp enabled
      const channels = {
        telegram: false,
        inApp: true, // Always enabled
        email: false,
        sms: false
      };

      // Check if ANY link has verified Telegram or valid email
      snapshot.forEach(doc => {
        const linkData = doc.data();

        // Enable Telegram if chatId exists (verified flag not required)
        if (linkData.telegram?.chatId) {
          channels.telegram = true;
        }

        // Enable email if valid email address exists
        if (linkData.email?.address && this.isValidEmail(linkData.email.address)) {
          channels.email = true;
        }
      });

      return channels;

    } catch (error) {
      console.error('Error initializing delivery channels:', error);
      // Fallback: only inApp
      return {
        telegram: false,
        inApp: true,
        email: false,
        sms: false
      };
    }
  }

  /**
   * Mark message as read on a specific channel
   * Auto-increments readCount
   * @param {string} messageId
   * @param {string} channel - 'telegram' | 'inApp' | 'email' | 'sms'
   * @param {string} [schoolId] - Required for hierarchical path
   * @returns {Promise<void>}
   */
  async markAsRead(messageId, channel, schoolId = null) {
    try {
      if (!Object.values(CHANNELS).includes(channel)) {
        throw new Error(`Invalid channel: ${channel}`);
      }

      // Get message to extract schoolId if not provided
      let messageRef, messageDoc;

      if (!schoolId) {
        // Need to find schoolId first - query collectionGroup
        const messagesQuery = query(
          collectionGroup(db, 'messages'),
          where('__name__', '==', messageId),
          limit(1)
        );
        const snapshot = await getDocs(messagesQuery);
        if (snapshot.empty) {
          throw new Error(`Message not found: ${messageId}`);
        }
        messageDoc = snapshot.docs[0];
        schoolId = messageDoc.data().schoolId;
        messageRef = messageDoc.ref;
      } else {
        messageRef = this._getMessageDocRef(messageId, schoolId);
        messageDoc = await getDoc(messageRef);
      }

      if (!messageDoc.exists()) {
        throw new Error(`Message not found: ${messageId}`);
      }

      const message = messageDoc.data();

      // Check if channel is enabled
      if (!message.delivery[channel]?.enabled) {
        throw new Error(`Channel ${channel} not enabled for this message`);
      }

      // Check if already read
      if (message.delivery[channel]?.readAt) {
        console.log(`Message ${messageId} already read on ${channel}`);
        return;
      }

      // Update read timestamp and increment readCount
      const updates = {
        [`delivery.${channel}.readAt`]: serverTimestamp(),
        readCount: (message.readCount || 0) + 1,
        updatedAt: serverTimestamp()
      };

      // Compute new overall status by cloning delivery object
      const updatedDelivery = {
        ...message.delivery,
        [channel]: {
          ...message.delivery[channel],
          readAt: new Date()
        }
      };

      const newStatus = computeOverallStatus(updatedDelivery);
      if (newStatus !== message.status) {
        updates.status = newStatus;
      }

      await updateDoc(messageRef, updates);
      console.log(`Message ${messageId} marked as read on ${channel}`);

      // Track engagement (async, non-blocking)
      this.updateParentEngagement(
        message.parentId,
        message.studentId,
        channel,
        'read'
      ).catch(err => console.error('Failed to track read engagement:', err));

    } catch (error) {
      console.error('Error marking message as read:', error);
      throw new Error(`Failed to mark as read: ${error.message}`);
    }
  }

  /**
   * Record parent's response to an action
   * Auto-increments responseCount
   * @param {string} messageId
   * @param {string} channel - Which channel they responded on
   * @param {string} actionId - Which action they clicked
   * @param {any} value - Their response value
   * @param {string} [schoolId] - Required for hierarchical path
   * @returns {Promise<void>}
   */
  async recordResponse(messageId, channel, actionId, value, schoolId = null) {
    try {
      if (!Object.values(CHANNELS).includes(channel)) {
        throw new Error(`Invalid channel: ${channel}`);
      }

      // Get message to extract schoolId if not provided
      let messageRef, messageDoc;

      if (!schoolId) {
        // Query collectionGroup to find message and extract schoolId
        const messagesQuery = query(
          collectionGroup(db, 'messages'),
          where('__name__', '==', messageId),
          limit(1)
        );
        const snapshot = await getDocs(messagesQuery);
        if (snapshot.empty) {
          throw new Error(`Message not found: ${messageId}`);
        }
        messageDoc = snapshot.docs[0];
        schoolId = messageDoc.data().schoolId;
        messageRef = messageDoc.ref;
      } else {
        messageRef = this._getMessageDocRef(messageId, schoolId);
        messageDoc = await getDoc(messageRef);
      }

      if (!messageDoc.exists()) {
        throw new Error(`Message not found: ${messageId}`);
      }

      const message = messageDoc.data();

      // Use the shared transaction for central Domain actions on web and native.
      // A response must not overwrite transport still waiting to send.
      if (channel === 'inApp' && message.metadata?.governedEventDelivery === true) {
        const respond = httpsCallable(functions, 'respondToLegacyMessageAction');
        const result = await respond({ schoolId, messageId, actionId });
        return result.data;
      }

      // Validate action exists
      const action = message.actions?.find(a => a.id === actionId);
      if (!action) {
        throw new Error(`Action ${actionId} not found in message`);
      }

      // Check if channel is enabled
      if (!message.delivery[channel]?.enabled) {
        throw new Error(`Channel ${channel} not enabled for this message`);
      }

      // Check if already responded
      if (message.delivery[channel]?.respondedAt) {
        console.log(`Message ${messageId} already responded on ${channel}`);
        return;
      }

      // Update response and increment responseCount
      const updates = {
        [`delivery.${channel}.respondedAt`]: serverTimestamp(),
        [`delivery.${channel}.response`]: {
          actionId,
          value,
          timestamp: serverTimestamp()
        },
        responseCount: (message.responseCount || 0) + 1,
        updatedAt: serverTimestamp()
      };

      // Auto-mark as read if not already
      if (!message.delivery[channel]?.readAt) {
        updates[`delivery.${channel}.readAt`] = serverTimestamp();
        updates.readCount = (message.readCount || 0) + 1;
      }

      // Compute new overall status by cloning delivery object
      const updatedDelivery = {
        ...message.delivery,
        [channel]: {
          ...message.delivery[channel],
          respondedAt: new Date(),
          readAt: message.delivery[channel]?.readAt || new Date()
        }
      };

      const newStatus = computeOverallStatus(updatedDelivery);
      if (newStatus !== message.status) {
        updates.status = newStatus;
      }

      await updateDoc(messageRef, updates);
      console.log(`Response recorded for message ${messageId} on ${channel}`);

      // Calculate response time (time between message sent and response)
      const sentAt = message.delivery[channel]?.sentAt;
      const responseTimeMs = sentAt ? Date.now() - sentAt.toMillis() : null;

      // Track engagement (async, non-blocking)
      this.updateParentEngagement(
        message.parentId,
        message.studentId,
        channel,
        'responded',
        responseTimeMs
      ).catch(err => console.error('Failed to track response engagement:', err));

    } catch (error) {
      console.error('Error recording response:', error);
      throw new Error(`Failed to record response: ${error.message}`);
    }
  }

  /**
   * Get all messages for a parent (with filtering)
   * @param {string} parentId
   * @param {Object} [options] - { status, type, limit, unreadOnly }
   * @returns {Promise<Array>}
   */
  async getMessagesByParent(parentId, options = {}, schoolId = null) {
    try {
      // Build query constraints array: where() clauses BEFORE orderBy()
      const constraints = [where('parentId', '==', parentId)];

      // Apply where() filters first
      if (options.status) {
        constraints.push(where('status', '==', options.status));
      }

      if (options.type) {
        constraints.push(where('type', '==', options.type));
      }

      // Then orderBy
      constraints.push(orderBy('createdAt', 'desc'));

      // Finally limit
      if (options.limit) {
        constraints.push(limit(options.limit));
      }

      const messagesRef = schoolId
        ? this._getMessagesCollectionRef(schoolId)
        : collectionGroup(db, 'messages');
      const q = query(messagesRef, ...constraints);
      const snapshot = await getDocs(q);

      let messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Client-side filter for unread (since readCount requires client logic)
      if (options.unreadOnly) {
        messages = messages.filter(msg => msg.readCount === 0);
      }

      return messages;

    } catch (error) {
      console.error('Error fetching messages by parent:', error);
      throw new Error(`Failed to fetch messages: ${error.message}`);
    }
  }

  /**
   * Subscribe to real-time parent messages
   *
   * Sets up a Firestore onSnapshot listener that automatically updates
   * when messages are added, modified, or deleted.
   *
   * @param {string} parentId - Parent user ID
   * @param {Function} callback - Called with (messages, error) on updates
   * @param {Object} [options] - Query options
   * @param {string} [options.status] - Filter by status
   * @param {string} [options.type] - Filter by type
   * @param {number} [options.limit] - Max messages to return
   * @param {boolean} [options.unreadOnly] - Only unread messages
   * @returns {Function} unsubscribe function - call to stop listening
   *
   * @example
   * const unsubscribe = messageService.subscribeToParentMessages(
   *   parentId,
   *   (messages, error) => {
   *     if (error) {
   *       console.error('Error:', error);
   *     } else {
   *       setMessages(messages);
   *     }
   *   },
   *   { limit: 50 }
   * );
   *
   * // Later, cleanup:
   * unsubscribe();
   */
  subscribeToParentMessages(parentId, callback, options = {}, schoolId = null) {
    try {
      // Build query constraints array: where() clauses BEFORE orderBy()
      const constraints = [where('parentId', '==', parentId)];

      // Apply where() filters first
      if (options.status) {
        constraints.push(where('status', '==', options.status));
      }

      if (options.type) {
        constraints.push(where('type', '==', options.type));
      }

      // Then orderBy
      constraints.push(orderBy('createdAt', 'desc'));

      // Finally limit
      if (options.limit) {
        constraints.push(limit(options.limit));
      }

      // Create query
      const messagesRef = schoolId
        ? this._getMessagesCollectionRef(schoolId)
        : collectionGroup(db, 'messages');
      const q = query(messagesRef, ...constraints);

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          let messages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Client-side filter for unread (since readCount requires client logic)
          if (options.unreadOnly) {
            messages = messages.filter(msg => msg.readCount === 0);
          }

          // Call callback with messages
          callback(messages, null);
        },
        (error) => {
          console.error('[MessageService] Subscription error:', error);
          // Call callback with error
          callback([], error);
        }
      );

      console.log(`[MessageService] Subscribed to messages for parent ${parentId}`);

      // Return unsubscribe function
      return unsubscribe;

    } catch (error) {
      console.error('Error setting up message subscription:', error);
      // Call callback with error immediately
      callback([], error);
      // Return no-op unsubscribe function
      return () => {};
    }
  }

  /**
   * Get count of unread messages across all channels
   * @param {string} parentId
   * @returns {Promise<number>}
   */
  async getUnreadCount(parentId) {
    try {
      const messages = await this.getMessagesByParent(parentId, { unreadOnly: true });
      return messages.length;

    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Update message status (internal use)
   * @param {string} messageId
   * @param {string} status
   * @returns {Promise<void>}
   */
  async updateMessageStatus(messageId, status, schoolId) {
    try {
      const messageRef = this._getMessageDocRef(messageId, schoolId);

      await updateDoc(messageRef, {
        status,
        updatedAt: serverTimestamp()
      });

    } catch (error) {
      console.error('Error updating message status:', error);
      throw new Error(`Failed to update status: ${error.message}`);
    }
  }

  /**
   * Get single message by ID
   * @param {string} messageId
   * @returns {Promise<Object|null>}
   */
  async getMessageById(messageId, schoolId) {
    try {
      const messageRef = this._getMessageDocRef(messageId, schoolId);
      const messageDoc = await getDoc(messageRef);

      if (!messageDoc.exists()) {
        return null;
      }

      return {
        id: messageDoc.id,
        ...messageDoc.data()
      };

    } catch (error) {
      console.error('Error fetching message by ID:', error);
      throw new Error(`Failed to fetch message: ${error.message}`);
    }
  }

  /**
   * Get messages by multiple filters (admin use)
   * @param {Object} filters - { schoolId, type, status, priority, startDate, endDate }
   * @returns {Promise<Array>}
   */
  async getMessages(filters) {
    try {
      // Build query constraints array: where() clauses BEFORE orderBy()
      const constraints = [];

      if (filters.type) {
        constraints.push(where('type', '==', filters.type));
      }

      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }

      if (filters.priority) {
        constraints.push(where('priority', '==', filters.priority));
      }

      // Then orderBy
      constraints.push(orderBy('createdAt', 'desc'));

      const messagesRef = filters.schoolId
        ? this._getMessagesCollectionRef(filters.schoolId)
        : collectionGroup(db, 'messages');
      const q = query(messagesRef, ...constraints);
      const snapshot = await getDocs(q);

      let messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Client-side date filtering (to avoid complex index requirements)
      if (filters.startDate) {
        const startDate = filters.startDate instanceof Date ? filters.startDate : new Date(filters.startDate);
        messages = messages.filter(msg => {
          const msgDate = msg.createdAt?.toDate ? msg.createdAt.toDate() : new Date(msg.createdAt);
          return msgDate >= startDate;
        });
      }

      if (filters.endDate) {
        const endDate = filters.endDate instanceof Date ? filters.endDate : new Date(filters.endDate);
        messages = messages.filter(msg => {
          const msgDate = msg.createdAt?.toDate ? msg.createdAt.toDate() : new Date(msg.createdAt);
          return msgDate <= endDate;
        });
      }

      return messages;

    } catch (error) {
      console.error('Error fetching messages with filters:', error);
      throw new Error(`Failed to fetch messages: ${error.message}`);
    }
  }

  /**
   * Update parent engagement metrics after message event
   * Called automatically when messages are sent, read, or responded to
   * @param {string} parentId
   * @param {string} studentId
   * @param {string} channel - 'telegram' | 'inApp' | 'email' | 'sms'
   * @param {string} eventType - 'sent' | 'read' | 'responded'
   * @param {number} [responseTimeMs] - Time to respond (for 'responded' events)
   * @returns {Promise<void>}
   */
  async updateParentEngagement(parentId, studentId, channel, eventType, responseTimeMs = null) {
    try {
      // Find the parent-student link
      const linksQuery = query(
        collection(db, this.parentLinksCollection),
        where('parentId', '==', parentId),
        where('studentId', '==', studentId),
        where('status', '==', 'active'),
        limit(1)
      );

      const snapshot = await getDocs(linksQuery);

      if (snapshot.empty) {
        console.warn(`No active link found for parent ${parentId} and student ${studentId}`);
        return;
      }

      const linkDoc = snapshot.docs[0];
      const linkData = linkDoc.data();

      // Initialize engagement metrics if not present
      const engagementMetrics = linkData.engagementMetrics || {
        telegram: { messagesSent: 0, messagesRead: 0, responsesReceived: 0, avgResponseTimeMs: 0, lastMessageAt: null },
        inApp: { messagesSent: 0, messagesRead: 0, responsesReceived: 0, avgResponseTimeMs: 0, lastMessageAt: null },
        email: { messagesSent: 0, messagesRead: 0, responsesReceived: 0, avgResponseTimeMs: 0, lastMessageAt: null },
        sms: { messagesSent: 0, messagesRead: 0, responsesReceived: 0, avgResponseTimeMs: 0, lastMessageAt: null }
      };

      const channelMetrics = engagementMetrics[channel] || {
        messagesSent: 0,
        messagesRead: 0,
        responsesReceived: 0,
        avgResponseTimeMs: 0,
        lastMessageAt: null
      };

      // Update based on event type
      const updates = {};

      switch (eventType) {
        case 'sent':
          updates[`engagementMetrics.${channel}.messagesSent`] = (channelMetrics.messagesSent || 0) + 1;
          updates[`engagementMetrics.${channel}.lastMessageAt`] = serverTimestamp();
          break;

        case 'read':
          updates[`engagementMetrics.${channel}.messagesRead`] = (channelMetrics.messagesRead || 0) + 1;
          break;

        case 'responded':
          updates[`engagementMetrics.${channel}.responsesReceived`] = (channelMetrics.responsesReceived || 0) + 1;

          // Update average response time if provided
          if (responseTimeMs !== null) {
            const currentAvg = channelMetrics.avgResponseTimeMs || 0;
            const currentCount = channelMetrics.responsesReceived || 0;
            const newAvg = ((currentAvg * currentCount) + responseTimeMs) / (currentCount + 1);
            updates[`engagementMetrics.${channel}.avgResponseTimeMs`] = Math.round(newAvg);
          }
          break;

        default:
          console.warn(`Unknown engagement event type: ${eventType}`);
          return;
      }

      // Update the document
      await updateDoc(doc(db, this.parentLinksCollection, linkDoc.id), updates);
      console.log(`Updated engagement metrics for parent ${parentId} on ${channel}: ${eventType}`);

    } catch (error) {
      console.error('Error updating parent engagement:', error);
      // Don't throw - engagement tracking is non-critical
    }
  }

  /**
   * Get engagement metrics for a parent across all channels
   * @param {string} parentId
   * @param {string} studentId
   * @returns {Promise<Object|null>} Engagement metrics object
   */
  async getParentEngagement(parentId, studentId) {
    try {
      const linksQuery = query(
        collection(db, this.parentLinksCollection),
        where('parentId', '==', parentId),
        where('studentId', '==', studentId),
        where('status', '==', 'active'),
        limit(1)
      );

      const snapshot = await getDocs(linksQuery);

      if (snapshot.empty) {
        return null;
      }

      const linkData = snapshot.docs[0].data();
      return linkData.engagementMetrics || null;

    } catch (error) {
      console.error('Error getting parent engagement:', error);
      return null;
    }
  }

  /**
   * Validate email address format
   * @param {string} email - Email address to validate
   * @returns {boolean} True if valid email format
   */
  isValidEmail(email) {
    if (!email || typeof email !== 'string') {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// Create singleton instance
const messageService = new MessageService();

export default messageService;
